import axios, { AxiosError } from 'axios';
import { isInNativeTab, requestReauthentication } from '../utils/nativeTab';

// Get API URL from environment variable, with fallbacks for local/production
export const getApiBaseUrl = (): string => {
  const envApiUrl = process.env.REACT_APP_API_BASE_URL;
  if (envApiUrl) {
    return envApiUrl;
  }

  if (window.location.hostname === 'localhost') {
    return process.env.REACT_APP_API_LOCAL_URL || 'http://localhost:5068';
  }

  return process.env.REACT_APP_API_PRODUCTION_URL || 'https://narcisevo.azurewebsites.net';
};

const baseURL = getApiBaseUrl();

const axiosInstance = axios.create({
  baseURL,
  timeout: 30000,
  withCredentials: true, // Send cookies with every request
});

// CSRF token management
let csrfToken: string | null = null;

export const getCsrfToken = (): string | null => csrfToken;

export const initializeCsrf = async (): Promise<void> => {
  try {
    const response = await axiosInstance.get('/auth/csrf-token');
    csrfToken = response.data?.token || null;
  } catch {
    console.warn('[CSRF] Failed to fetch CSRF token');
  }
};

// Request interceptor - add CSRF token for state-changing methods
axiosInstance.interceptors.request.use(
  (config) => {
    const method = config.method?.toUpperCase();
    if (csrfToken && (method === 'POST' || method === 'PUT' || method === 'DELETE' || method === 'PATCH')) {
      config.headers['X-CSRF-TOKEN'] = csrfToken;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor - handle common error scenarios
axiosInstance.interceptors.response.use(
  (response) => response,
  (error: AxiosError) => {
    if (error.response) {
      const status = error.response.status;
      switch (status) {
        case 401:
          if (isInNativeTab()) {
            const reauthRequired = error.response.headers['x-auth-reauth-required'];
            if (reauthRequired) {
              // Silent OIDC already failed, ask platform to re-authenticate
              requestReauthentication(window.location.pathname + window.location.search);
              error.message = 'Authentication required - requesting platform re-authentication';
            } else {
              // Cookie expired, try silent OIDC flow again
              window.location.href = `${baseURL}/auth/login?returnUrl=${encodeURIComponent(window.location.pathname + window.location.search)}&nativeTab=true`;
              error.message = 'Authentication required - attempting silent re-authentication';
            }
          } else {
            window.location.href = `${baseURL}/auth/login?returnUrl=${encodeURIComponent(window.location.pathname + window.location.search)}`;
            error.message = 'Authentication required - redirecting to sign in';
          }
          break;
        case 403:
          console.warn('Access forbidden - insufficient permissions');
          error.message = 'You do not have permission to perform this action';
          break;
        case 429:
          console.warn('Rate limit exceeded - please try again later');
          error.message = 'Too many requests - please wait and try again';
          break;
        case 500:
        case 502:
        case 503:
        case 504:
          console.error(`Server error (${status}) - please try again later`);
          error.message = 'A server error occurred - please try again later';
          break;
      }
    } else if (error.request) {
      console.error('Network error - no response from server');
      error.message = 'Unable to reach the server - please check your connection';
    }
    return Promise.reject(error);
  }
);

export default axiosInstance;
